/**
 * `<ThemeSettings>` — the Settings panel that lets the user pick a theme.
 *
 * The theme is client-only state (UX, not corpus content) — backed by
 * `useUiStore`'s `theme` / `setTheme`, which persist to `localStorage`
 * directly (no backend round-trip). The five values mirror the `Theme` union
 * in the store: slate (default — v4 Slate+Gold), forest (legacy v1 marketing
 * green), dark (pure-neutral), light (paper inversion), system (resolves at
 * apply time via `<ThemeProvider>`'s OS-preference listener).
 *
 * v4 refit (2026-05-18, GAP_ANALYSIS.md §ST1 + §ST2): replaces the legacy
 * `.seg` segmented control with the v4 `.theme-grid` of `.theme-swatch`es —
 * each swatch has a real preview block showing the theme's bg + accent color,
 * with the theme name in mono below. The card chrome is the new SettingsCard
 * primitive. Slate is now the default (was Forest); the swatch order reflects
 * that.
 */

import { SettingsCard } from './SettingsCard';
import { useUiStore } from '@/stores';
// Remaster U1: the selectable-theme list moved to the shared catalog so this
// panel and the CommandPalette can never drift apart again.
import { THEME_CATALOG as THEMES } from '@/theme/themeCatalog';

export function ThemeSettings(): JSX.Element {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);

  const activeHint = THEMES.find((t) => t.id === theme)?.hint ?? '';

  return (
    <SettingsCard
      id="theme"
      title="Theme"
      desc="Color palette + accent semantics. Ember is the default."
      fullWidth
    >
      <div className="theme-grid" role="radiogroup" aria-label="Theme">
        {THEMES.map((t) => (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={theme === t.id}
            aria-pressed={theme === t.id}
            data-theme={t.id}
            className="theme-swatch"
            onClick={() => setTheme(t.id)}
            title={t.hint}
          >
            <div className="theme-swatch__preview" aria-hidden="true" />
            <span className="theme-swatch__name">{t.label}</span>
          </button>
        ))}
      </div>
      <div className="theme-note" aria-live="polite">
        {activeHint}
      </div>
    </SettingsCard>
  );
}
